import React, { useState, useEffect } from 'react';
import { Card, Table, Spin, Alert, Tag, Radio, Button, Row, Col, Tooltip, Modal, Select } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { instance } from "../../../API/API";

const StaffMonthlyHoursTable = ({selectedYear, mode}) => {
    const [data, setData] = useState([]);
    const [months, setMonths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dailyDetails, setDailyDetails] = useState([]);
    const [dailyModalVisible, setDailyModalVisible] = useState(false);
    const [dailyLoading, setDailyLoading] = useState(false);
    const [currentStaff, setCurrentStaff] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(null);

    // Состояния для переключателей


    useEffect(() => {
        fetchStaffMonthlyHours();
    }, [mode, selectedYear]);

    const fetchStaffMonthlyHours = async () => {
        try {
            setLoading(true);

            const params = {
                mode: mode,
                year: selectedYear
            };

            const response = await instance.get('analytics/staff_monthly_hours.php', { params, timeout: 20000 });

            if (response.data.success) {
                setData(response.data.data);
                setMonths(response.data.months);
            } else {
                throw new Error('Invalid response format');
            }
        } catch (err) {
            console.error('Error fetching staff monthly hours:', err);
            setError('Ошибка загрузки данных по сотрудникам');
        } finally {
            setLoading(false);
        }
    };

    const fetchDailyDetails = async (staffId, staffName, monthName) => {
        try {
            setDailyLoading(true);
            setCurrentStaff(staffName);
            setCurrentMonth(monthName);

            // Получаем номер месяца из названия
            const monthNames = {
                'Янв': 1, 'Фев': 2, 'Мар': 3, 'Апр': 4, 'Май': 5, 'Июн': 6,
                'Июл': 7, 'Авг': 8, 'Сен': 9, 'Окт': 10, 'Ноя': 11, 'Дек': 12
            };

            const monthNumber = monthNames[monthName];

            const response = await instance.get('analytics/staff_daily_details.php', {
                params: {
                    staff_id: staffId,
                    month: monthNumber,
                    year: selectedYear,
                    mode: mode
                }
            });

            if (response.data.success) {
                setDailyDetails(response.data.data);
                setDailyModalVisible(true);
            } else {
                throw new Error('Invalid daily details format');
            }
        } catch (err) {
            console.error('Error fetching daily details:', err);
        } finally {
            setDailyLoading(false);
        }
    };

    // Функция для конвертации секунд в часы
    const secondsToHours = (seconds) => {
        return (seconds / 3600).toFixed(1);
    };

    const getTitle = () => {
        let modeText;
        switch (mode) {
            case 'work':
                modeText = 'Отработанные часы';
                break;
            case 'downtime':
                modeText = 'Время простоев';
                break;
            case 'overtime':
                modeText = 'Переработки';
                break;
            default:
                modeText = 'Отработанные часы';
        }

        const total = data.reduce((accumulator, current) => accumulator + current.total, 0);
        const totalHours = secondsToHours(total);

        return `${modeText} по сотрудникам за ${selectedYear} год: ${totalHours} ч`;
    };

    const getTagColor = (seconds, currentMode) => {
        const hours = seconds / 3600;

        switch (currentMode) {
            case 'work':
                return hours >= 160 ? 'green' : hours >= 120 ? 'orange' : 'blue';
            case 'downtime':
                return hours >= 40 ? 'red' : hours >= 20 ? 'orange' : 'blue';
            case 'overtime':
                return hours >= 40 ? 'red' : hours >= 20 ? 'orange' : 'blue';
            default:
                return 'blue';
        }
    };



    // Создаем колонки для таблицы
    const columns = [
        {
            title: 'Сотрудник',
            dataIndex: 'staff_name',
            key: 'staff_name',
            fixed: 'left',
            width: 200,
            sorter: (a, b) => a.staff_name.localeCompare(b.staff_name),
        },
        ...months.map(month => ({
            title: month.name,
            dataIndex: ['months', month.name],
            key: month.name,
            width: 100,
            sorter: (a, b) => (a.months[month.name] || 0) - (b.months[month.name] || 0),
            render: (seconds, record) => {
                const hours = secondsToHours(seconds);
                return (
                    <Tooltip title="Нажмите для детализации по дням">
                        <div
                            style={{
                                cursor: 'pointer',
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '4px'
                            }}
                            onClick={() => fetchDailyDetails(record.staff_id, record.staff_name, month.name)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f0f0f0';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            {seconds > 0 ? (
                                <Tag color={getTagColor(seconds, mode)}>
                                    {hours}
                                </Tag>
                            ) : (
                                <span style={{color: '#ccc'}}>0</span>
                            )}
                        </div>
                    </Tooltip>
                );
            }
        })),
        {
            title: 'Всего за год',
            dataIndex: 'total',
            key: 'total',
            fixed: 'right',
            width: 120,
            sorter: (a, b) => a.total - b.total,
            render: (total) => {
                const hours = secondsToHours(total);
                return (
                    <Tag color={getTagColor(total, mode)}>
                        {hours}
                    </Tag>
                );
            },
        }
    ];

    // Колонки для модального окна с детализацией по дням (в часах)
    const dailyColumns = [
        {
            title: 'Дата',
            dataIndex: 'date',
            key: 'date',
            width: 120,
            render: (date) => new Date(date).toLocaleDateString('ru-RU')
        },
        {
            title: 'Отработано часов',
            dataIndex: 'work_seconds',
            key: 'work_seconds',
            width: 140,
            render: (seconds) => <Tag color="blue">{secondsToHours(seconds)}</Tag>
        },
        {
            title: 'Время простоя (ч)',
            dataIndex: 'downtime_seconds',
            key: 'downtime_seconds',
            width: 140,
            render: (seconds) => seconds > 0 ? <Tag color="orange">{secondsToHours(seconds)}</Tag> : <span style={{color: '#ccc'}}>0</span>
        },
        {
            title: 'Переработка (ч)',
            dataIndex: 'overtime_seconds',
            key: 'overtime_seconds',
            width: 140,
            render: (seconds) => seconds > 0 ? <Tag color="red">{secondsToHours(seconds)}</Tag> : <span style={{color: '#ccc'}}>0</span>
        },
        {
            title: 'Норма (ч)',
            dataIndex: 'expected_seconds',
            key: 'expected_seconds',
            width: 120,
            render: (seconds) => <Tag color="purple">{secondsToHours(seconds)}</Tag>
        }
    ];

    const calculateDailyTotals = () => {
        if (dailyDetails.length === 0) return { work: 0, downtime: 0, overtime: 0, total: 0 };

        return {
            work: dailyDetails.reduce((sum, item) => sum + item.work_seconds, 0),
            downtime: dailyDetails.reduce((sum, item) => sum + item.downtime_seconds, 0),
            overtime: dailyDetails.reduce((sum, item) => sum + item.overtime_seconds, 0),
            total: dailyDetails.reduce((sum, item) => sum + item.expected_seconds, 0)
        };
    };

    if (loading) {
        return (
            <Card style={{
                width: '100%',
                minHeight: '400px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Spin size="large" />
            </Card>
        );
    }

    if (error) {
        return (
            <Card style={{ width: '100%', minHeight: '400px' }}>
                <Alert message={error} type="error" />
            </Card>
        );
    }

    const dailyTotals = calculateDailyTotals();

    return (
        <>
            <Card
                title={getTitle()}
                style={{
                    width: '100%',
                    background: 'white',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}

            >
                <Table
                    columns={columns}
                    dataSource={data.filter(el => el.total > 0).sort ((a, b) => a.staff_name.localeCompare(b.staff_name))}
                    rowKey="staff_id"
                    scroll={{ x: months.length * 100 + 320 }}
                    pagination={false}
                    size="middle"
                />
            </Card>

            {/* Модальное окно с детализацией по дням */}
            <Modal
                title={
                    <div>
                        <InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        Детализация по дням: {currentStaff} - {currentMonth} {selectedYear}
                    </div>
                }
                open={dailyModalVisible}
                onCancel={() => setDailyModalVisible(false)}
                footer={null}
                width={700}
            >
                {dailyLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin />
                    </div>
                ) : (
                    <Table
                        columns={dailyColumns}
                        dataSource={dailyDetails}
                        rowKey="date"
                        pagination={false}
                        size="small"
                        scroll={{ y: 400 }}
                        summary={() => (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0}>Итого</Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}>
                                        <Tag color="blue">
                                            {secondsToHours(dailyTotals.work)}
                                        </Tag>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}>
                                        <Tag color="orange">
                                            {secondsToHours(dailyTotals.downtime)}
                                        </Tag>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                        <Tag color="red">
                                            {secondsToHours(dailyTotals.overtime)}
                                        </Tag>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4}>
                                        <Tag color="purple">
                                            {secondsToHours(dailyTotals.total)}
                                        </Tag>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                )}
            </Modal>
        </>
    );
};

export default StaffMonthlyHoursTable;